/**
 * ProductsAdmin.tsx — Admin > Products.
 *
 * The signed-in account's product categories and the products filed under
 * them, on one page (CategoryBoard), under a column header (Name | in store |
 * Facilities | Price) as the old Products table had:
 *   - categories are listed alphabetically by title (ignoring case) and are
 *     not dragged; "Uncategorized" comes last, and only when it holds products;
 *   - a category's products are listed A–Z; drag one onto another category
 *     to move it there (2026-10-07, user: the board files, it no longer
 *     orders). The move writes quoteItemCostsCategoryID, and lngOrder puts it
 *     last in the new category's saved order (lngOrder, then name).
 * Never shown, in either view: "----" separator products, and separator
 * categories (three or more hyphens in a row — isSeparatorTitle) with their
 * products.
 * Every drop saves at once. Categories and products are added, edited and
 * (soft) deleted here too, from icon buttons on the right of every row.
 * "Show deleted" lists the deleted ones as well (muted, Restore only) and
 * turns drag off. Single account: apiClient sends the account in session;
 * there is no account picker.
 *
 * The toolbar carries the corp Products filters (no account filter):
 * Facility, Categories, Product types (incl. "No Product Type"), Store
 * availability and Audience ("Available To"). A searched or filtered board
 * shows only the categories holding a match, with drag off — a partial list
 * is never saved as the order. "Add category" sits top right in the page
 * header, as on every Admin list; products are added from a category's +.
 *
 * The add / edit modals copy the corporate dashboard's layout (field order,
 * sections, labels, hint text, switches, the image tile, the required /
 * suggestion strips above the footer). Delete is never offered inside them —
 * only from the row icons.
 *
 * Existing products keep the old modal's two immediate saves: facility
 * switches auto-save, and "Available in Store" asks before it saves (gated by
 * level 4 or the Manage Store Availability permission); its history is listed
 * at the bottom of the Availability box. A new product's store switch is part
 * of the form and goes with Create (same gate on the API). Everything else
 * saves with Update, and an edit tracks which fields changed (an inline
 * "modified" pill with a per-field revert, Revert, and a prompt before
 * closing with unsaved changes).
 *
 * Corp-parity (the live corporate dashboard Products page is the spec):
 *   - the image is the selected product TYPE's default image, chosen with the
 *     entity image picker; required when a type is set and the product is in
 *     the store;
 *   - product types can be added or updated from beside the selector (the
 *     shared ProductTypeFormModal); a new one is selected;
 *   - Stock, "Apply 20%" member pricing and a "Member price is not set" hint;
 *   - category names are checked against live categories while typing;
 *   - a category can be cloned with its products.
 *
 * Locked (plan 2026-10-10-001 KTD6, R9) keeps main's editing rule on this
 * page, silently (R18): the board has no drag, Add, edit, clone, delete or
 * restore (the shared blocks), and a product opens read-only except its
 * facility checklist and its store switch (main's rule: level 4 or Manage
 * Store Availability, and the store requirements). Neither saves on its own
 * there: Save (on only once one of them changed) sends just the changed
 * `facilityIDs` / `publicstore`, which the API accepts alone. Its product
 * type stays read-only — no Add / Update beside it.
 *
 * The product form has no Category field (user, 2026-10-14): products are
 * filed only by dragging them on the board. A new product goes in the
 * category whose + opened the form, and an edit never sends a category.
 */
import { useEffect, useState, useMemo, useCallback, useRef, type ReactNode } from 'react';
import { Store, Package, Plus, RotateCcw, Copy, AlertTriangle, ChevronDown, ChevronRight, Tag, ImageIcon, FileText } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { Button } from '@/components/ui/Button';
import { SpinnerOverlay, Spinner } from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { CategoryBoard, boardDragHint, type BoardCategory } from '@/components/admin/CategoryBoard';
import { RichDescriptionField } from '@/components/admin/RichTextDialog';
import { compareTitles, isSeparatorTitle } from '@/components/admin/categoryBoardState';
import { EntityImageTile } from '@/components/admin/EntityImageTile';
import { AdminBoardPage, AdminListEmpty, AdminListToolbar } from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminLockException } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { FlagIcon, SummaryIcon } from '@/components/admin/CorpBadges';
import { ListFilterBar, anyFilterActive, type ListFilterConfig } from '@/components/admin/ListFilterBar';
import {
  DESCRIPTION_CLASS,
  DialogField,
  DialogFooterLayout,
  DialogHeading,
  LABEL_CLASS,
  SwitchField,
  CheckboxList,
  fieldClasses,
  type FieldState,
} from '@/components/admin/DialogChrome';
import { ProductTypeFormModal, isDeletedProductType } from '@/components/admin/ProductTypeForm';
import type { ProductTypeDetail } from '@/services/productTypes.service';
import { getDefaultEntityImageUrl } from '@/services/entityImages.service';
import { toast } from '@/store/toastStore';
import * as productsSvc from '@/services/products.service';
import type { ProductFull, ProductStoreEvent } from '@/services/products.service';
import * as categoriesSvc from '@/services/productCategories.service';
import type { ProductItemCategory } from '@/services/productCategories.service';
import * as adminSvc from '@/services/admin.service';
import type { Facility } from '@/services/admin.service';
import * as inventorySvc from '@/services/inventory.service';
import type { ProductTypeLookupEntry } from '@/services/inventory.service';
import * as paymentsSvc from '@/services/payments.service';
import type { ShippingFee } from '@/services/payments.service';
import * as membershipsSvc from '@/services/memberships.service';
import type { MembershipTemplate } from '@/services/memberships.service';
import { getErrorMessage } from '@/services/apiClient';
import { useAuth } from '@/hooks/useAuth';
import { PERMISSION } from '@/lib/permissions';
import { cn, parseCurrency, formatCurrency, formatDateTime } from '@/lib/utils';

// ─── Helpers ───────────────────────────────────────────────────────────────────

function isActive(val: number | boolean | string | null | undefined): boolean {
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val === 1;
  return val === '1' || val === 'true';
}

function getProductName(p: ProductFull): string {
  return p.strItem ?? '';
}

/**
 * A product whose category was deleted counts as deleted (matches V4's
 * getFullProductsList, which requires the category to be active too).
 * Uncategorized products stay visible; so do responses from an older API
 * build that doesn't send category.active.
 */
function isCategoryActive(p: ProductFull): boolean {
  const cat = p.relationships?.category;
  if (!cat || cat.active === undefined || cat.active === null) return true;
  return isActive(cat.active);
}

/** Shown on the board: active, category not deleted, not a "----" separator. */
function isListed(p: ProductFull): boolean {
  return isActive(p.active) && isCategoryActive(p) && !isSeparator(p);
}

/** "----" rows are placeholder separators, never shown. */
function isSeparator(p: ProductFull): boolean {
  return /----/.test(getProductName(p));
}

/** Soft-deleted product (active 0). Module-level so the board's memo holds. */
function isProductDeleted(p: ProductFull): boolean {
  return !isActive(p.active);
}

/** A category with no active flag (older API build) counts as active. */
function isCategoryRowActive(c: ProductItemCategory): boolean {
  return c.active === undefined || c.active === null ? true : isActive(c.active);
}

/** Both lists, each product once (the later list wins). */
function mergeProducts(...lists: ProductFull[][]): ProductFull[] {
  const byId = new Map<number, ProductFull>();
  for (const list of lists) for (const p of list) byId.set(p.quoteItemCostID, p);
  return [...byId.values()];
}

/** The product's category, or null for none. Module-level so the board's memo holds. */
function getCategoryId(p: ProductFull): number | null {
  const id = p.quoteItemCostsCategoryID ?? p.relationships?.category?.quoteItemCostsCategoryID ?? null;
  return id ? Number(id) : null;
}

function getRecordId(p: ProductFull): number {
  return p.quoteItemCostID;
}

/** lngOrder, then name, then id — rows tied on lngOrder (often 0) sort by name. */
function compareProducts(a: ProductFull, b: ProductFull): number {
  return (
    (Number(a.lngOrder) || 0) - (Number(b.lngOrder) || 0) ||
    getProductName(a).localeCompare(getProductName(b)) ||
    a.quoteItemCostID - b.quoteItemCostID
  );
}

/** Alphabetical by title, ignoring case (as the old Products table listed them). */
function compareCategories(a: ProductItemCategory, b: ProductItemCategory): number {
  return compareTitles(a.title, b.title) || a.quoteItemCostsCategoryID - b.quoteItemCostsCategoryID;
}

/** Filed under a separator category ("--------"), by id or by the category the API embedded. */
function inSeparatorCategory(p: ProductFull, separatorIds: Set<number>): boolean {
  const id = getCategoryId(p);
  return (id !== null && separatorIds.has(id)) || isSeparatorTitle(p.relationships?.category?.title);
}

function getPrice(p: ProductFull): number {
  return parseCurrency(p.curRate);
}

function hasProductType(p: ProductFull): boolean {
  const ptId = p.relationships?.productTypeRelation?.productTypeID;
  return ptId != null && ptId !== 0;
}

/** publicStatus comes back as 1 or '1' depending on the database driver; both mean in store. */
function isAvailableInStore(p: ProductFull): boolean {
  const status: unknown = p.relationships?.productAddon?.publicStatus;
  return status === true || String(status ?? '') === '1';
}

function getProductTypeImageUrl(p: ProductFull): string | null {
  // relationships.productTypeRelation.images, which is where ProductsController
  // puts them. This read `relationships.images` through a Record assertion —
  // a key that does not exist there, so it always returned null and no product
  // ever showed its image. The assertion is also what the compiler rejected:
  // an interface with no index signature does not overlap Record<string, …>.
  const images = p.relationships?.productTypeRelation?.images;
  if (!images || images.length === 0) return null;
  const defaultImg = images.find((i) => i.defaultUrl === 1 || i.defaultUrl === '1');
  return (defaultImg ?? images[0])?.productUrl ?? null;
}

/**
 * Mirrors corporate dashboard logic: when facilityID is 0/"0" and no explicit
 * productFacilities, the product is available at ALL facilities.
 */
function getProductFacilityIds(p: ProductFull, allFacilities: Facility[]): number[] {
  const explicit = (p.relationships?.productFacilities ?? []).map((f) => f.facilityID);
  if (explicit.length > 0) return explicit;

  const rawFacilityId = p.facilityID;
  if (rawFacilityId === 0 || rawFacilityId === null) {
    return allFacilities.filter((f) => f.active).map((f) => f.facilityId);
  }
  if (rawFacilityId != null) return [rawFacilityId];
  return [];
}

function storeEventLabel(e: ProductStoreEvent): string {
  return e.action === 'added' ? 'Added to store' : 'Removed from store';
}

function storeEventActor(e: ProductStoreEvent): string {
  const who = e.userName ?? (e.source === 'api_client' ? 'Another system' : 'Unknown user');
  return e.source === 'corp_dashboard' ? `${who} (Corporate dashboard)` : who;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Today as YYYY-MM-DD in the browser's calendar (verifydate is a plain date). */
function todayDate(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Where a new product lands in a category: after the last one. */
function nextOrderIn(products: ProductFull[], categoryId: number | null): number {
  let max = 0;
  for (const p of products) {
    if (getCategoryId(p) === categoryId && isActive(p.active)) max = Math.max(max, Number(p.lngOrder) || 0);
  }
  return max + 1;
}

function setProductFacilities(p: ProductFull, facilityIds: number[], facilities: Facility[]): ProductFull {
  const newFacilities = facilityIds.map((id) => {
    const existing = p.relationships.productFacilities.find((f) => f.facilityID === id);
    if (existing) return existing;
    const fac = facilities.find((f) => f.facilityId === id);
    return {
      facilityID: id,
      facilityName: fac?.facilityName ?? null,
      pivot: { accountID: p.accountID, status: null, createUserID: null, stopDate: null, createDate: null },
    };
  });
  return {
    ...p,
    facilityID:
      facilityIds.length === 0
        ? null
        : facilityIds.length === facilities.filter((f) => f.active).length
          ? 0
          : p.facilityID,
    relationships: { ...p.relationships, productFacilities: newFacilities },
  };
}

function setProductStore(p: ProductFull, available: boolean): ProductFull {
  const publicStatus = available ? '1' : '0';
  return {
    ...p,
    relationships: {
      ...p.relationships,
      productAddon: p.relationships.productAddon
        ? { ...p.relationships.productAddon, publicStatus }
        : {
            productAddonID: 0,
            quoteItemCostID: p.quoteItemCostID,
            productTypeID: null,
            accountID: p.accountID,
            availabilityStatus: null,
            publicStatus,
            curMemberRate: null,
            status: null,
            updateDate: null,
            createDate: null,
          },
    },
  };
}

/** The product's type id, or null. */
function getProductTypeId(p: ProductFull): number | null {
  const id = p.relationships?.productTypeRelation?.productTypeID || p.relationships?.productAddon?.productTypeID || null;
  return id ? Number(id) : null;
}

/** Show `url` as the image of every product carrying the type. */
function setProductTypeImage(p: ProductFull, url: string): ProductFull {
  return {
    ...p,
    // `images` rides along on the list response; it is not in the typed shape.
    relationships: { ...p.relationships, images: [{ productUrl: url, defaultUrl: 1 }] } as unknown as ProductFull['relationships'],
  };
}

/**
 * The LIVE category already called `title` (trimmed, case-insensitive), not
 * counting `excludeId` — the API's DUPLICATE_TITLE rule.
 */
function findCategoryConflict(
  categories: ProductItemCategory[],
  title: string,
  excludeId?: number | null,
): ProductItemCategory | undefined {
  const key = title.trim().toLowerCase();
  if (!key) return undefined;
  return categories.find(
    (c) =>
      isCategoryRowActive(c) &&
      c.title.trim().toLowerCase() === key &&
      (excludeId == null || c.quoteItemCostsCategoryID !== excludeId),
  );
}

function categoryConflictMessage(title: string, match: ProductItemCategory): string {
  return `This account already has a category named "${title.trim()}" (#${match.quoteItemCostsCategoryID}). Select it instead of adding another.`;
}

// ─── Product form ──────────────────────────────────────────────────────────────

type AvailableTo = '0' | '1' | '2';

interface ProductForm {
  name: string;
  categoryId: number | null;
  productTypeId: number | null;
  description: string;
  retailPrice: string;
  memberPrice: string;
  availabilityStatus: AvailableTo;
  availableToAllMemberships: boolean;
  membershipTemplateIds: number[];
  /** productType: 0 = Standard, 1 = Gift Credit. */
  group: '0' | '1';
  unit: string;
  taxable: boolean;
  isInventory: boolean;
  parLevel: string;
  verifyInterval: string;
  /** quantityRemaining. */
  stock: string;
  shippingFeeId: number;
  allowPriceOverride: boolean;
  /** Store-only settings, shown while the product is in the store. */
  saleType: '0' | '1';
  sendLead: boolean;
  requestableByNonMembers: boolean;
}

type FormErrors = Partial<
  Record<'name' | 'retailPrice' | 'memberPrice' | 'parLevel' | 'verifyInterval' | 'stock' | 'image', string>
>;

/** Field labels, for the revert buttons. */
const FIELD_LABELS: Record<keyof ProductForm, string> = {
  name: 'Name',
  categoryId: 'Category',
  productTypeId: 'Product type',
  description: 'Description',
  retailPrice: 'Retail price',
  memberPrice: 'Member price',
  availabilityStatus: 'Available to',
  availableToAllMemberships: 'Available to all memberships',
  membershipTemplateIds: 'Memberships',
  group: 'Group',
  unit: 'Unit',
  taxable: 'Taxable',
  isInventory: 'Show in inventory',
  parLevel: 'PAR level',
  verifyInterval: 'Verification interval',
  stock: 'Stock',
  shippingFeeId: 'Shipping fee',
  allowPriceOverride: 'Allow price override',
  saleType: 'Store purchase',
  sendLead: 'Send lead',
  requestableByNonMembers: 'Requestable by non-members',
};

function sameValue(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
  }
  return a === b;
}

/** The fields of `form` that differ from `saved`. */
function changedFields(form: ProductForm | null, saved: ProductForm | null): Set<keyof ProductForm> {
  const out = new Set<keyof ProductForm>();
  if (!form || !saved) return out;
  for (const k of Object.keys(FIELD_LABELS) as (keyof ProductForm)[]) {
    if (!sameValue(form[k], saved[k])) out.add(k);
  }
  return out;
}

function emptyForm(categoryId: number | null): ProductForm {
  return {
    name: '',
    categoryId,
    productTypeId: null,
    description: '',
    retailPrice: '',
    memberPrice: '',
    availabilityStatus: '0',
    availableToAllMemberships: true,
    membershipTemplateIds: [],
    group: '0',
    unit: '1',
    taxable: false,
    isInventory: false,
    parLevel: '',
    verifyInterval: '',
    stock: '',
    shippingFeeId: 0,
    allowPriceOverride: false,
    saleType: '0',
    sendLead: false,
    requestableByNonMembers: false,
  };
}

function money(value: unknown): string {
  if (value == null || value === '') return '';
  return parseCurrency(value).toFixed(2);
}

function formFromProduct(p: ProductFull): ProductForm {
  const addon = p.relationships?.productAddon;
  const status = String(addon?.availabilityStatus ?? '0');
  const allMemberships = addon?.availableToAllMemberships;
  return {
    name: getProductName(p),
    categoryId: getCategoryId(p),
    productTypeId: p.relationships?.productTypeRelation?.productTypeID || addon?.productTypeID || null,
    description: p.strDescription ?? '',
    retailPrice: money(p.curRate),
    memberPrice: addon?.curMemberRate ? money(addon.curMemberRate) : '',
    availabilityStatus: status === '1' || status === '2' ? status : '0',
    availableToAllMemberships: allMemberships == null ? true : isActive(allMemberships),
    membershipTemplateIds: (p.relationships?.membershipTemplates ?? []).map((m) => Number(m.membershipTemplateID)),
    group: String(p.productType ?? 0) === '1' ? '1' : '0',
    unit: p.strUnit ?? '',
    taxable: isActive(p.taxable),
    isInventory: isActive(p.isInventory),
    parLevel: p.parLevel != null ? String(p.parLevel) : '',
    verifyInterval: p.verifyInterval != null ? String(p.verifyInterval) : '',
    stock: p.quantityRemaining != null ? String(p.quantityRemaining) : '',
    shippingFeeId: Number(addon?.shippingFeeID ?? 0) || 0,
    allowPriceOverride: isActive(addon?.allowPriceOverride),
    saleType: String(addon?.saleType ?? 0) === '1' ? '1' : '0',
    sendLead: isActive(addon?.sendLead),
    requestableByNonMembers: !!addon?.requestableByNonMembers,
  };
}

const PRICE_RE = /^\d+(\.\d{0,2})?$/;
const WHOLE_RE = /^\d+$/;
const INTEGER_RE = /^-?\d+$/;

const IMAGE_REQUIRED_MESSAGE = 'Image is required when a product type is selected and the product is available in the store.';

function validate(form: ProductForm, image: { inStore: boolean; imageUrl: string | null }): FormErrors {
  const errors: FormErrors = {};
  if (form.stock.trim() !== '' && !INTEGER_RE.test(form.stock.trim())) {
    errors.stock = 'Enter a whole number.';
  }
  if (form.productTypeId && image.inStore && !image.imageUrl) {
    errors.image = IMAGE_REQUIRED_MESSAGE;
  }
  if (form.name.trim() === '') errors.name = 'Enter a product name.';
  if (form.retailPrice.trim() !== '' && !PRICE_RE.test(form.retailPrice.trim().replace(/^\$/, ''))) {
    errors.retailPrice = 'Enter a price, e.g. 25.00.';
  }
  if (form.memberPrice.trim() !== '' && !PRICE_RE.test(form.memberPrice.trim().replace(/^\$/, ''))) {
    errors.memberPrice = 'Enter a price, e.g. 20.00.';
  }
  if (form.parLevel.trim() !== '' && !WHOLE_RE.test(form.parLevel.trim())) {
    errors.parLevel = 'Enter a whole number (0 or more).';
  }
  if (form.verifyInterval.trim() !== '' && !WHOLE_RE.test(form.verifyInterval.trim())) {
    errors.verifyInterval = 'Enter a whole number of days (0 or more).';
  }
  return errors;
}

function priceOut(value: string, blank: string): string {
  const v = value.trim().replace(/^\$/, '');
  return v === '' ? blank : Number(v).toFixed(2);
}

/**
 * Settings that only apply under a condition are reset when it is not met, so
 * the API and outside store apps never read a flag that contradicts the
 * product (the corporate dashboard's applicableStoreSettings): Store purchase
 * and Send lead need the product in the store; requests by non-members also
 * need a Members Only audience.
 */
function applicableStoreSettings(
  form: Pick<ProductForm, 'saleType' | 'sendLead' | 'requestableByNonMembers' | 'availabilityStatus'>,
  inStore: boolean,
): { saleType: number; sendLead: boolean; requestableByNonMembers: boolean } {
  return {
    saleType: inStore ? Number(form.saleType) || 0 : 0,
    sendLead: inStore ? form.sendLead : false,
    requestableByNonMembers: inStore && form.availabilityStatus === '1' ? form.requestableByNonMembers : false,
  };
}

/**
 * The addon fields the API writes together. The API rewrites every addon
 * column whenever any one is sent — publicStatus included — so the CURRENT
 * store flag always goes along; otherwise a plain edit would pull the product
 * out of the store.
 */
function addonPayload(form: ProductForm, inStore: boolean): Record<string, unknown> {
  return {
    productTypeID: form.productTypeId ?? 0,
    curMemberRate: priceOut(form.memberPrice, ''),
    availabilityStatus: form.availabilityStatus,
    publicstore: inStore ? '1' : '0',
    availableToAllMemberships: form.availableToAllMemberships,
    membershipTemplateIDs: form.availableToAllMemberships ? [] : form.membershipTemplateIds,
    shippingFeeID: form.shippingFeeId,
    allowPriceOverride: form.allowPriceOverride,
    ...applicableStoreSettings(form, inStore),
  };
}

interface Lookups {
  /** Every product type in the account, deleted ones too (status 0). */
  productTypes: ProductTypeLookupEntry[];
  shippingFees: ShippingFee[];
  membershipTemplates: MembershipTemplate[];
}

// ─── Change tracking ───────────────────────────────────────────────────────────

/** The "modified" pill with its revert button, on a field changed since load. */
function ModifiedPill({ label, onRevert }: { label: string; onRevert: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
      modified
      <button
        type="button"
        aria-label={`Revert ${label}`}
        title="Revert"
        className="hover:text-blue-900"
        onClick={(e) => {
          e.preventDefault();
          onRevert();
        }}
      >
        <RotateCcw className="h-2.5 w-2.5" aria-hidden />
      </button>
    </span>
  );
}

interface Tracking {
  changed: Set<keyof ProductForm>;
  revert: (field: keyof ProductForm) => void;
}

/** The pill for `field`, inline after its label (corp's modifiedBadge), or null. */
function badgeFor(tracking: Tracking, field: keyof ProductForm): ReactNode {
  return tracking.changed.has(field) ? (
    <ModifiedPill label={FIELD_LABELS[field]} onRevert={() => tracking.revert(field)} />
  ) : null;
}

// ─── Live checks (corp modalValidation) ────────────────────────────────────────

/**
 * What blocks Create / Update, and what is only suggested — listed in the
 * footer strips while the form is open, as the corporate dashboard does.
 */
function liveChecks(args: {
  form: ProductForm;
  inStore: boolean;
  imageUrl: string | null;
  facilityCount: number;
  totalFacilities: number;
}): { errors: string[]; suggestions: string[] } {
  const { form, inStore, imageUrl, facilityCount, totalFacilities } = args;
  const errors: string[] = [];
  const suggestions: string[] = [];
  if (!form.name.trim()) errors.push('Product name is required');
  if (inStore && !form.productTypeId) errors.push('Product type is required when available in store');
  if (form.productTypeId && inStore && !imageUrl) {
    errors.push('Image is required when product type is selected and available in store');
  }
  if (totalFacilities > 0 && facilityCount === 0) suggestions.push('No facilities selected');
  if (facilityCount > 0 && facilityCount < totalFacilities) {
    suggestions.push(`${facilityCount} of ${totalFacilities} facilities selected`);
  }
  if (memberPriceMissing(form)) suggestions.push('Member price is not set');
  return { errors, suggestions };
}

// ─── Product modal (add + edit) ────────────────────────────────────────────────

interface ProductModalProps {
  /** null = a new product. */
  product: ProductFull | null;
  /**
   * A new product's category: the one whose + opened the form. There is no
   * category field — products are filed by dragging them on the board.
   */
  defaultCategoryId: number | null;
  /** That category's title, for the description. */
  categoryTitle?: string;
  facilities: Facility[];
  lookups: Lookups;
  nextOrder: (categoryId: number | null) => number;
  onClose: () => void;
  onSaved: (product: ProductFull, isNew: boolean) => void;
  onFacilitiesChanged: (productId: number, facilityIds: number[]) => void;
  onStoreChanged: (productId: number, available: boolean) => void;
  /** A product type was added or updated from beside the selector. */
  onProductTypeSaved: (saved: ProductTypeDetail, isNew: boolean, imageUrl: string | null) => void;
  /** A product type's image was changed with the picker (already saved). */
  onProductTypeImageChanged: (productTypeId: number, url: string) => void;
}

type TypeModalState = { kind: 'new' } | { kind: 'edit'; id: number } | null;

function ProductModal({
  product,
  defaultCategoryId,
  categoryTitle,
  facilities,
  lookups,
  nextOrder,
  onClose,
  onSaved,
  onFacilitiesChanged,
  onStoreChanged,
  onProductTypeSaved,
  onProductTypeImageChanged,
}: ProductModalProps) {
  const isNew = product === null;
  // Locked page (KTD6): only the facilities and the store switch change, and
  // both wait for Save. Only an existing product is ever opened there.
  const locked = useAdminPageLevel() === 'locked';
  const { hasMinUserLevel, hasPermission } = useAuth();
  // Mirrors ManagesInventoryAccess::assertStoreAvailabilityChange; the server
  // is authoritative, this only keeps the switch honest.
  const canManageStore = hasMinUserLevel(4) || hasPermission(PERMISSION.MANAGE_STORE_AVAILABILITY);

  const activeFacilities = facilities.filter((f) => f.active);
  const initialFacIds = product ? getProductFacilityIds(product, facilities) : activeFacilities.map((f) => f.facilityId);
  const [selectedFacIds, setSelectedFacIds] = useState<number[]>(initialFacIds);
  // Existing product: the SAVED store flag (changed only through the prompt).
  const [storeStatus, setStoreStatus] = useState(product ? isAvailableInStore(product) : false);
  // Locked: the store value asked for, sent with Save.
  const [lockedStore, setLockedStore] = useState(storeStatus);
  // New product: part of the form, sent with Create (the API gates it the same way).
  const [newInStore, setNewInStore] = useState(false);
  // The value the user asked for, held until they confirm. Nothing is saved
  // and the switch does not move while this is set.
  const [pendingStore, setPendingStore] = useState<boolean | null>(null);
  const [storeHistory, setStoreHistory] = useState<ProductStoreEvent[] | null>(null);
  const [storeHistoryFailed, setStoreHistoryFailed] = useState(false);
  // Collapsed until the user asks for it; loaded on first open.
  const [storeHistoryOpen, setStoreHistoryOpen] = useState(false);

  // The list omits unit, PAR level, verification interval and memberships, so
  // an existing product's form waits for the full record.
  const [form, setForm] = useState<ProductForm | null>(isNew ? emptyForm(defaultCategoryId) : null);
  // The form as loaded: what "modified" and Revert compare against.
  const [savedForm, setSavedForm] = useState<ProductForm | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [typeModal, setTypeModal] = useState<TypeModalState>(null);

  // The image is the selected product TYPE's default image.
  const [typeImageUrl, setTypeImageUrl] = useState<string | null>(product ? getProductTypeImageUrl(product) : null);

  const facilitySaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFacIds = useRef<number[] | null>(null);
  const prevFacRef = useRef(initialFacIds);

  const productId = product?.quoteItemCostID ?? null;
  const canToggleStore = product !== null && hasProductType(product) && !!typeImageUrl;
  const productName = product ? getProductName(product) : '';
  const selectedTypeId = form?.productTypeId ?? null;
  const inStore = isNew ? newInStore : storeStatus;

  useEffect(() => {
    if (productId === null) return;
    let cancelled = false;
    productsSvc
      .getProduct(productId)
      .then((full) => {
        if (cancelled) return;
        const loaded = formFromProduct(full);
        setForm(loaded);
        setSavedForm(loaded);
      })
      .catch(() => {
        if (!cancelled) setDetailFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  // Follow the selected type's image.
  useEffect(() => {
    if (form === null) return;
    if (!selectedTypeId) {
      setTypeImageUrl(null);
      return;
    }
    let cancelled = false;
    getDefaultEntityImageUrl('product-type', selectedTypeId)
      .then((url) => {
        if (!cancelled) setTypeImageUrl(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Only a different type re-reads; `form` itself changes on every key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypeId, form === null]);

  const changed = useMemo(() => (isNew ? new Set<keyof ProductForm>() : changedFields(form, savedForm)), [isNew, form, savedForm]);

  // Locked: what differs from the saved product (the facilities as the page
  // holds them, the store flag as saved) is all Save sends.
  const lockedFacilitiesChanged = locked && !isNew && !sameValue(selectedFacIds, initialFacIds);
  const lockedStoreChanged = locked && !isNew && lockedStore !== storeStatus;
  const dirty = locked ? lockedFacilitiesChanged || lockedStoreChanged : changed.size > 0;

  function patch(next: Partial<ProductForm>) {
    setForm((prev) => (prev ? { ...prev, ...next } : prev));
    const touched = Object.keys(next) as (keyof FormErrors)[];
    if ('productTypeId' in next) touched.push('image');
    if (touched.some((k) => errors[k])) {
      setErrors((prev) => {
        const copy = { ...prev };
        for (const k of touched) delete copy[k];
        return copy;
      });
    }
  }

  const tracking: Tracking = {
    changed,
    revert: (field) => {
      if (!savedForm) return;
      const value = savedForm[field];
      patch({ [field]: Array.isArray(value) ? [...value] : value } as Partial<ProductForm>);
    },
  };

  function revertAll() {
    if (locked) {
      setSelectedFacIds(initialFacIds);
      setLockedStore(storeStatus);
      return;
    }
    if (!savedForm) return;
    setForm({ ...savedForm, membershipTemplateIds: [...savedForm.membershipTemplateIds] });
    setErrors({});
  }

  // ── Facilities: auto-save on an existing product; part of the form on a new
  // one, and on a Locked page (sent with Save).

  function changeFacilities(ids: number[]) {
    setSelectedFacIds(ids);
    if (!isNew && !locked) debouncedFacilitySave(ids);
  }

  function debouncedFacilitySave(ids: number[]) {
    if (facilitySaveTimer.current) clearTimeout(facilitySaveTimer.current);
    pendingFacIds.current = ids;
    facilitySaveTimer.current = setTimeout(() => {
      facilitySaveTimer.current = null;
      pendingFacIds.current = null;
      void saveFacilities(ids);
    }, 600);
  }

  /** Save a facility change still waiting on its debounce (on Update). */
  function flushFacilitySave() {
    if (!facilitySaveTimer.current || !pendingFacIds.current) return;
    clearTimeout(facilitySaveTimer.current);
    facilitySaveTimer.current = null;
    const ids = pendingFacIds.current;
    pendingFacIds.current = null;
    void saveFacilities(ids);
  }

  async function saveFacilities(ids: number[]) {
    if (productId === null) return;
    const prev = prevFacRef.current;
    if (ids.length === prev.length && ids.every((id) => prev.includes(id))) return;
    prevFacRef.current = ids;
    try {
      await productsSvc.updateProduct(productId, { facilityIDs: ids });
      onFacilitiesChanged(productId, ids);
      toast.success('Facilities updated');
    } catch {
      toast.error('Failed to update facilities');
    }
  }

  useEffect(() => {
    return () => {
      if (facilitySaveTimer.current) {
        clearTimeout(facilitySaveTimer.current);
        facilitySaveTimer.current = null;
      }
    };
  }, []);

  // ── Store availability (existing products: asks first, saves at once).

  const loadStoreHistory = useCallback(async () => {
    if (productId === null) return;
    try {
      setStoreHistory(await productsSvc.getStoreHistory(productId));
      setStoreHistoryFailed(false);
    } catch {
      setStoreHistoryFailed(true);
    }
  }, [productId]);

  useEffect(() => {
    if (storeHistoryOpen) void loadStoreHistory();
  }, [storeHistoryOpen, loadStoreHistory]);

  // Runs only from the confirm prompt. Never throws: ConfirmDialog closes
  // itself after this resolves, and a refused save keeps the switch as it was.
  async function saveStoreStatus(val: boolean) {
    if (productId === null) return;
    try {
      await productsSvc.updateProduct(productId, { publicstore: val ? '1' : '0' });
      setStoreStatus(val);
      onStoreChanged(productId, val);
      toast.success(val ? 'Now available in store' : 'Removed from store');
      if (storeHistoryOpen) void loadStoreHistory();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The store change could not be saved. Please try again.'));
    }
  }

  // ── Product type image and the Add / Update type modal.

  function handleImageSelected(url: string) {
    setTypeImageUrl(url);
    if (errors.image) setErrors((prev) => ({ ...prev, image: undefined }));
    if (selectedTypeId) onProductTypeImageChanged(selectedTypeId, url);
  }

  function handleTypeSaved(saved: ProductTypeDetail, isNewType: boolean, imageUrl: string | null) {
    onProductTypeSaved(saved, isNewType, imageUrl);
    if (isNewType) patch({ productTypeId: saved.productTypeID });
    if (imageUrl) setTypeImageUrl(imageUrl);
    setTypeModal(null);
  }

  // ── Live checks (the footer strips) and save.

  const checks = form
    ? liveChecks({
        form,
        inStore,
        imageUrl: typeImageUrl,
        facilityCount: selectedFacIds.length,
        totalFacilities: activeFacilities.length,
      })
    : { errors: [], suggestions: [] };

  async function handleSave() {
    if (!form) return;
    // The footer already lists these and disables the button; checked again
    // here because the button state is only a convenience.
    if (checks.errors.length > 0) return;
    const found = validate(form, { inStore, imageUrl: typeImageUrl });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const base: Record<string, unknown> = {
      strItem: form.name.trim(),
      strDescription: form.description,
      curRate: priceOut(form.retailPrice, '0'),
      productType: Number(form.group),
      ...(form.unit.trim() !== '' ? { strUnit: form.unit.trim() } : {}),
      taxable: form.taxable,
      isInventory: form.isInventory,
    };

    setSaving(true);
    try {
      let saved: ProductFull;
      if (product === null) {
        saved = await productsSvc.createProduct({
          ...base,
          strUnit: form.unit.trim() || '1',
          sglQuantity: '1',
          strType: 'Product',
          nonDiscID: 0,
          quantityRemaining: form.stock.trim() === '' ? 0 : Number(form.stock),
          verifydate: todayDate(),
          // Same defaults the corporate dashboard creates with.
          verifyInterval: form.verifyInterval.trim() === '' ? 30 : Number(form.verifyInterval),
          parLevel: form.parLevel.trim() === '' ? 10 : Number(form.parLevel),
          facilityID: 0,
          facilityIDs: selectedFacIds,
          quoteItemCostsCategoryID: form.categoryId,
          lngOrder: nextOrder(form.categoryId),
          active: true,
          productTypeStatus: '1',
          productTypePublicStatus: 0,
          ...addonPayload(form, newInStore),
        });
      } else {
        flushFacilitySave();
        // No category here: the board files products, and sending the one the
        // form opened with would undo a drag made while it was open.
        saved = await productsSvc.updateProduct(product.quoteItemCostID, {
          ...base,
          ...(form.parLevel.trim() !== '' ? { parLevel: Number(form.parLevel) } : {}),
          ...(form.verifyInterval.trim() !== '' ? { verifyInterval: Number(form.verifyInterval) } : {}),
          ...(form.stock.trim() !== '' ? { quantityRemaining: Number(form.stock) } : {}),
          ...addonPayload(form, storeStatus),
        });
      }
      onSaved(saved, product === null);
      toast.success(product === null ? 'Product added' : 'Product saved');
    } catch (err) {
      toast.error(getErrorMessage(err, isNew ? 'The product could not be added.' : 'The product could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Locked Save (KTD6): only the changed `facilityIDs` and/or `publicstore`.
   * The API takes each alone without touching the product's other fields, and
   * still applies its own store-permission check. A refusal keeps the dialog
   * open with the changes, so they can be retried or reverted.
   */
  async function handleLockedSave() {
    if (productId === null || !dirty) return;
    const payload: Record<string, unknown> = {
      ...(lockedFacilitiesChanged ? { facilityIDs: selectedFacIds } : {}),
      ...(lockedStoreChanged ? { publicstore: lockedStore ? '1' : '0' } : {}),
    };
    setSaving(true);
    try {
      await productsSvc.updateProduct(productId, payload);
      if (lockedFacilitiesChanged) onFacilitiesChanged(productId, selectedFacIds);
      if (lockedStoreChanged) onStoreChanged(productId, lockedStore);
      toast.success('Product saved');
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The product could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  // ── Options.

  const productTypeOptions = useMemo(() => {
    // Deleted types are not offered (the product's own current type stays).
    const live = lookups.productTypes.filter((t) => !isDeletedProductType(t));
    const counts = new Map<string, number>();
    for (const t of live) {
      const n = (t.productTypeName ?? '').trim().toLowerCase();
      counts.set(n, (counts.get(n) ?? 0) + 1);
    }
    // Keyed by id, never by name: names repeat across the lookup.
    const opts = live.map((t) => {
      const name = (t.productTypeName ?? '').trim() || `Type #${t.productTypeID}`;
      const repeated = (counts.get((t.productTypeName ?? '').trim().toLowerCase()) ?? 0) > 1;
      return { value: t.productTypeID, label: repeated ? `${name} (#${t.productTypeID})` : name };
    });
    const current = form?.productTypeId;
    if (current && !opts.some((o) => o.value === current)) {
      const name =
        lookups.productTypes.find((t) => t.productTypeID === current)?.productTypeName ??
        product?.relationships?.productTypeRelation?.productTypeName;
      opts.unshift({ value: current, label: name ? `${name} (#${current})` : `Type #${current}` });
    }
    return [{ value: 0, label: 'No product type' }, ...opts];
  }, [lookups.productTypes, form?.productTypeId, product]);

  const shippingOptions = useMemo(() => {
    const opts = lookups.shippingFees.map((f) => ({
      value: f.shippingFeeID,
      label: `${f.name} — ${formatCurrency(Number(f.amount ?? 0))}`,
    }));
    const current = form?.shippingFeeId ?? 0;
    if (current && !opts.some((o) => o.value === current)) {
      opts.unshift({ value: current, label: `(deleted fee #${current})` });
    }
    return [{ value: 0, label: 'No shipping fee' }, ...opts];
  }, [lookups.shippingFees, form?.shippingFeeId]);

  const imageRequired = !!selectedTypeId && inStore;

  // ── Store switch + history, placed by ProductFields in the Availability box.

  const storeSwitch = isNew ? (
    <div className="grid gap-1">
      <SwitchField
        id="product-available-in-store"
        label="Available in Store"
        ariaLabel="Available in Store"
        checked={newInStore}
        onChange={setNewInStore}
        disabled={!canManageStore}
      />
      {!canManageStore && (
        <p className="text-xs leading-snug text-slate-500">
          Only managers can change this. Ask your account admin for the Manage Store Availability permission.
        </p>
      )}
    </div>
  ) : (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        {/* A Locked exception: on a Locked page this switch still changes, and waits for Save. */}
        <AdminLockException>
          <SwitchField
            id="product-available-in-store"
            label="Available in Store"
            ariaLabel="Available in Store"
            checked={locked ? lockedStore : storeStatus}
            onChange={(val) => (locked ? setLockedStore(val) : setPendingStore(val))}
            disabled={!canToggleStore || !canManageStore}
          />
        </AdminLockException>
        <Store
          className={cn('h-4 w-4 shrink-0', (locked ? lockedStore : storeStatus) ? 'text-orange-500' : 'text-slate-300')}
          aria-hidden
        />
      </div>
      {!canToggleStore ? (
        <p className="text-xs leading-snug text-amber-700">
          This product is missing requirements to be added to the store. Please contact your admin.
        </p>
      ) : !canManageStore ? (
        <p className="text-xs leading-snug text-slate-500">
          Only managers can change this. Ask your account admin for the Manage Store Availability permission.
        </p>
      ) : locked ? null : (
        <p className="text-xs leading-snug text-slate-500">Asks before it changes, then saves at once.</p>
      )}
    </div>
  );

  const storeHistoryBlock = isNew ? null : (
    <div className="grid gap-2 border-t border-slate-200 pt-3">
      <button
        type="button"
        className="flex w-fit items-center gap-1.5 text-left"
        aria-expanded={storeHistoryOpen}
        onClick={() => setStoreHistoryOpen((o) => !o)}
      >
        {storeHistoryOpen ? (
          <ChevronDown className="h-4 w-4 text-slate-500" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500" aria-hidden />
        )}
        <span className={LABEL_CLASS}>Store history</span>
      </button>
      {!storeHistoryOpen ? null : storeHistoryFailed ? (
        <p className="text-sm text-slate-500">Store history couldn't be loaded. Close and reopen this product to try again.</p>
      ) : storeHistory === null ? null : storeHistory.length === 0 ? (
        <p className="text-sm text-slate-400">No changes yet</p>
      ) : (
        <ul data-testid="store-history" className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 bg-white">
          {storeHistory.map((e, i) => (
            <li
              key={`${e.at}-${i}`}
              className="flex flex-col gap-0.5 px-3 py-2 text-sm sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
            >
              <span className="text-slate-700">
                <span className="font-medium">{storeEventLabel(e)}</span>
                <span className="text-slate-500"> · {storeEventActor(e)}</span>
              </span>
              <span className="shrink-0 text-xs text-slate-500">{formatDateTime(e.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // ── Facilities (left column), corp's list with switch rows.

  const facState: FieldState =
    activeFacilities.length > 0 && selectedFacIds.length < activeFacilities.length ? 'warning' : 'none';

  return (
    // AdminFormModal: corp's footer (required / suggestion strips, Unsaved
    // changes + Revert, Cancel, Create / Update), a prompt before closing with
    // unsaved edits, and Escape never closes it — prompts, the image picker and
    // the product type form open on top, and every open Modal listens on
    // document. On a Locked page it is read-only except the two Locked
    // exceptions below, and Save sends only them (`lockedSave`).
    <AdminFormModal
      title={isNew ? 'Add New Product' : 'Edit Product'}
      description={
        !isNew && form === null && !detailFailed
          ? 'Loading product details...'
          : isNew
            ? categoryTitle
              ? `Add a new product to ${categoryTitle}`
              : 'Add a new product to one or more facilities'
            : 'Update product information'
      }
      isNew={isNew}
      dirty={dirty}
      saving={saving}
      errors={form ? checks.errors : []}
      suggestions={form ? checks.suggestions : []}
      // Locked saves only the facilities and the store flag, so it does not wait for the form.
      canSave={locked || form !== null}
      onSave={() => void (locked ? handleLockedSave() : handleSave())}
      onRevertAll={revertAll}
      onClose={onClose}
      size="xl"
      lockedSave
    >
      <div className="flex flex-col gap-6 md:flex-row">
        {/* Left column: Image + Facilities */}
        <div className="space-y-4 md:w-48 md:shrink-0">
          <EntityImageTile
            entityType="product-type"
            entityId={selectedTypeId}
            imageUrl={typeImageUrl}
            onSelected={handleImageSelected}
            label="Product Image"
            required={imageRequired}
            error={errors.image}
            disabled={!selectedTypeId || form === null}
            disabledText="Select a product type to display an image"
          />

          {/* A Locked exception: on a Locked page the facilities still change, and wait for Save. */}
          <AdminLockException>
            <div className={cn('space-y-2', fieldClasses.wrapper(facState))}>
              {facState === 'warning' && (
                <p className={cn(fieldClasses.message('warning'), 'flex items-center gap-1')}>
                  <AlertTriangle className="h-3 w-3" aria-hidden />
                  {selectedFacIds.length === 0
                    ? 'No facilities selected'
                    : `${selectedFacIds.length} of ${activeFacilities.length} selected`}
                </p>
              )}
              <span className={cn(LABEL_CLASS, fieldClasses.label(facState))}>Facilities</span>
              {!isNew && !locked && <p className="text-xs text-slate-500">Changes save as you switch them.</p>}
              {activeFacilities.length === 0 ? (
                <p className="py-1 text-xs text-slate-500">This account has no active facilities.</p>
              ) : (
                <CheckboxList
                  idPrefix="fac"
                  items={activeFacilities.map((f) => ({ id: f.facilityId, label: f.facilityName }))}
                  selected={selectedFacIds}
                  onChange={changeFacilities}
                />
              )}
            </div>
          </AdminLockException>
        </div>

        {/* Right column: all form fields */}
        <div className="min-w-0 flex-1">
          {form === null ? (
            detailFailed ? (
              <p className="text-sm text-slate-500">
                This product's details couldn't be loaded. Close and reopen it to edit its fields.
              </p>
            ) : (
              <div className="flex flex-col items-center justify-center space-y-4 py-12">
                <Spinner />
                <p className="text-sm text-slate-500">Loading product details...</p>
              </div>
            )
          ) : (
            <ProductFields
              form={form}
              errors={errors}
              patch={patch}
              tracking={tracking}
              locked={locked}
              inStore={inStore}
              storeSwitch={storeSwitch}
              storeHistory={storeHistoryBlock}
              productTypeOptions={productTypeOptions}
              shippingOptions={shippingOptions}
              membershipTemplates={lookups.membershipTemplates}
              onAddType={() => setTypeModal({ kind: 'new' })}
              onUpdateType={() => (selectedTypeId ? setTypeModal({ kind: 'edit', id: selectedTypeId }) : undefined)}
            />
          )}
        </div>
      </div>

      {typeModal && (
        <ProductTypeFormModal
          key={typeModal.kind === 'edit' ? `edit-${typeModal.id}` : 'new'}
          productTypeId={typeModal.kind === 'edit' ? typeModal.id : null}
          allTypes={lookups.productTypes}
          initialName={typeModal.kind === 'new' ? form?.name ?? '' : undefined}
          onClose={() => setTypeModal(null)}
          onSaved={handleTypeSaved}
          onImageChanged={(id, url) => {
            onProductTypeImageChanged(id, url);
            if (id === selectedTypeId) setTypeImageUrl(url);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={pendingStore !== null}
        onClose={() => setPendingStore(null)}
        onConfirm={() => (pendingStore === null ? undefined : saveStoreStatus(pendingStore))}
        title={pendingStore ? 'Add to store?' : 'Remove from store?'}
        message={
          pendingStore
            ? `${productName} will be available for purchase in the store.`
            : `${productName} will no longer be available for purchase in the store.`
        }
        confirmLabel={pendingStore ? 'Add to store' : 'Remove from store'}
        variant="warning"
      />
    </AdminFormModal>
  );
}

// ─── Product fields ────────────────────────────────────────────────────────────

const AVAILABLE_TO_OPTIONS: { value: AvailableTo; label: string }[] = [
  { value: '0', label: 'Everyone' },
  { value: '1', label: 'Members Only' },
  { value: '2', label: 'Non-Members Only' },
];

const MEMBERSHIP_SCOPE_OPTIONS: { value: 'all' | 'selected'; label: string }[] = [
  { value: 'all', label: 'All memberships' },
  { value: 'selected', label: 'Selected memberships' },
];

const GROUP_OPTIONS: { value: '0' | '1'; label: string }[] = [
  { value: '0', label: 'Standard' },
  { value: '1', label: 'Gift Credit' },
];

const SALE_TYPE_OPTIONS: { value: '0' | '1'; label: string }[] = [
  { value: '0', label: 'For Sale' },
  { value: '1', label: 'Requestable' },
];

/** Retail less 20%, as the corporate dashboard's "Apply 20%". */
function memberPriceAt20(retail: string): string | null {
  const r = parseFloat(retail.trim().replace(/^\$/, ''));
  return Number.isFinite(r) && r > 0 ? (r * 0.8).toFixed(2) : null;
}

/** A retail price is set but the member price is blank or zero. */
function memberPriceMissing(form: ProductForm): boolean {
  const retail = parseFloat(form.retailPrice.trim().replace(/^\$/, ''));
  const member = parseFloat(form.memberPrice.trim().replace(/^\$/, ''));
  return Number.isFinite(retail) && retail !== 0 && (!Number.isFinite(member) || member === 0);
}

/**
 * The right column, in the corporate dashboard's order: Product Name, Product
 * Type (+ Add / Update), Description, Retail |
 * Member price, the Availability box (Available in Store; when on, Store
 * Purchase + Send Lead; Available To, with Requestable by Non-Members beside it
 * when it applies), Memberships (Members Only), Group + Allow Price Override,
 * Shipping Fee, Stock | Unit | Taxable | Inventory, then PAR / Verification.
 * Corp's Category field is left out: products are filed by dragging them on
 * the board, and a new one goes in the category whose + opened the form.
 */
function ProductFields({
  form,
  errors,
  patch,
  tracking,
  locked,
  inStore,
  storeSwitch,
  storeHistory,
  productTypeOptions,
  shippingOptions,
  membershipTemplates,
  onAddType,
  onUpdateType,
}: {
  form: ProductForm;
  errors: FormErrors;
  patch: (next: Partial<ProductForm>) => void;
  tracking: Tracking;
  /**
   * A Locked page: these fields are read-only (the modal locks them), so the
   * buttons that change them (Add / Update type, Apply 20%) and the required /
   * suggested markers on what is missing are left out.
   */
  locked: boolean;
  inStore: boolean;
  storeSwitch: ReactNode;
  storeHistory: ReactNode;
  productTypeOptions: { value: number; label: string }[];
  shippingOptions: { value: number; label: string }[];
  membershipTemplates: MembershipTemplate[];
  onAddType: () => void;
  onUpdateType: () => void;
}) {
  const badge = (field: keyof ProductForm) => badgeFor(tracking, field);
  const missName = !locked && !form.name.trim();
  const missType = !locked && inStore && !form.productTypeId;
  const warnMember = !locked && memberPriceMissing(form);

  return (
    <div className="space-y-4">
      {/* Product Name */}
      <DialogField
        label="Product Name"
        htmlFor="product-name"
        required
        state={missName ? 'error' : 'none'}
        labelHint={missName ? '— required' : undefined}
        badge={badge('name')}
      >
        <Input
          id="product-name"
          value={form.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder="Product name"
          error={errors.name}
          className={fieldClasses.input(missName ? 'error' : 'none')}
        />
      </DialogField>

      {/* Product Type */}
      <DialogField
        label="Product Type"
        htmlFor="product-type"
        required={inStore}
        state={missType ? 'error' : 'none'}
        labelHint={missType ? '— required for store' : undefined}
        badge={badge('productTypeId')}
      >
        <div className="flex items-center gap-2">
          <Select<number>
            id="product-type"
            options={productTypeOptions}
            value={form.productTypeId ?? 0}
            onChange={(v) => patch({ productTypeId: v ? v : null })}
            placeholder="Select product type"
            searchable
            wrapperClassName="min-w-0 flex-1"
          />
          {!locked && (
            <>
              <Button variant="secondary" size="sm" aria-label="Add new product type" onClick={onAddType}>
                Add
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label="Update selected product type"
                onClick={onUpdateType}
                disabled={!form.productTypeId}
              >
                Update
              </Button>
            </>
          )}
        </div>
      </DialogField>

      {/* Description */}
      <DialogField label="Description" htmlFor="product-description" badge={badge('description')}>
        <RichDescriptionField
          id="product-description"
          value={form.description}
          onChange={(html) => patch({ description: html })}
          dialogTitle="Edit Product Description"
        />
      </DialogField>

      {/* Retail Price | Member Price */}
      <div className="grid gap-4 sm:grid-cols-2">
        <DialogField label="Retail Price ($)" htmlFor="product-retail-price" badge={badge('retailPrice')}>
          <Input
            id="product-retail-price"
            inputMode="decimal"
            placeholder="0.00"
            value={form.retailPrice}
            onChange={(e) => patch({ retailPrice: e.target.value })}
            error={errors.retailPrice}
          />
        </DialogField>
        <DialogField
          label="Member Price ($)"
          htmlFor="product-member-price"
          state={warnMember ? 'warning' : 'none'}
          topMessage={warnMember ? 'Suggestion: Set member price' : undefined}
          badge={badge('memberPrice')}
        >
          <div className="flex gap-2">
            <Input
              id="product-member-price"
              inputMode="decimal"
              placeholder="0.00"
              value={form.memberPrice}
              onChange={(e) => patch({ memberPrice: e.target.value })}
              error={errors.memberPrice}
              wrapperClassName="min-w-0 flex-1"
              className={fieldClasses.input(warnMember ? 'warning' : 'none')}
            />
            {!locked && (
              <Button
                variant="secondary"
                size="sm"
                className="h-9 shrink-0"
                title="Set the member price to the retail price less 20%"
                onClick={() => {
                  const next = memberPriceAt20(form.retailPrice);
                  if (next !== null) patch({ memberPrice: next });
                }}
              >
                Apply 20%
              </Button>
            )}
          </div>
        </DialogField>
      </div>

      {/* Availability: the store switch always at the top; the store-only
          settings directly under it while it is on; Available To either way. */}
      <div className="grid gap-3 rounded-md border border-slate-200 p-3" data-testid="availability-section">
        {storeSwitch}

        {inStore && (
          <div className="grid items-end gap-4 sm:grid-cols-3" data-testid="store-settings">
            <DialogField label="Store Purchase" htmlFor="product-sale-type" badge={badge('saleType')}>
              <Select<'0' | '1'>
                id="product-sale-type"
                options={SALE_TYPE_OPTIONS}
                value={form.saleType}
                onChange={(v) => patch({ saleType: v ?? '0' })}
              />
            </DialogField>
            <SwitchField
              id="product-send-lead"
              label="Send Lead"
              checked={form.sendLead}
              onChange={(v) => patch({ sendLead: v })}
              description="When purchased, create a lead."
              badge={badge('sendLead')}
              className="pb-1 sm:col-span-2"
            />
          </div>
        )}

        <div className="grid items-end gap-4 sm:grid-cols-3">
          <DialogField label="Available To" htmlFor="product-available-to" badge={badge('availabilityStatus')}>
            <Select<AvailableTo>
              id="product-available-to"
              options={AVAILABLE_TO_OPTIONS}
              value={form.availabilityStatus}
              onChange={(v) =>
                patch({ availabilityStatus: v ?? '0', ...(v === '1' ? { availableToAllMemberships: true } : {}) })
              }
            />
          </DialogField>
          {inStore && form.availabilityStatus === '1' && (
            <SwitchField
              id="product-requestable"
              label="Requestable by Non-Members"
              checked={form.requestableByNonMembers}
              onChange={(v) => patch({ requestableByNonMembers: v })}
              description="Non-members cannot buy it, but can request it."
              badge={badge('requestableByNonMembers')}
              className="pb-1 sm:col-span-2"
            />
          )}
        </div>

        {storeHistory}
      </div>

      {/* Membership availability (Members Only) */}
      {form.availabilityStatus === '1' && (
        <div className="grid gap-4 rounded-md border border-slate-200 p-3">
          <DialogField
            label="Available To Memberships"
            htmlFor="product-all-memberships"
            badge={badge('availableToAllMemberships')}
          >
            <Select<'all' | 'selected'>
              id="product-all-memberships"
              options={MEMBERSHIP_SCOPE_OPTIONS}
              value={form.availableToAllMemberships ? 'all' : 'selected'}
              onChange={(v) => patch({ availableToAllMemberships: v !== 'selected' })}
            />
          </DialogField>
          {!form.availableToAllMemberships && (
            <DialogField label="Memberships" htmlFor="product-memberships" badge={badge('membershipTemplateIds')}>
              {membershipTemplates.length === 0 ? (
                <p className="text-xs text-slate-500">This account has no memberships.</p>
              ) : (
                <SearchableMultiSelect
                  id="product-memberships"
                  options={membershipTemplates.map((t) => ({ value: String(t.templateId), label: t.title }))}
                  selectedValues={form.membershipTemplateIds.map(String)}
                  onSelectionChange={(values) => patch({ membershipTemplateIds: values.map(Number) })}
                  placeholder="Select memberships"
                  searchPlaceholder="Search memberships…"
                />
              )}
            </DialogField>
          )}
        </div>
      )}

      {/* Group | Allow Price Override */}
      <div className="grid items-end gap-4 sm:grid-cols-3">
        <DialogField label="Group" htmlFor="product-group" badge={badge('group')}>
          <Select<'0' | '1'>
            id="product-group"
            options={GROUP_OPTIONS}
            value={form.group}
            onChange={(v) => patch({ group: v ?? '0' })}
            placeholder="Select group"
          />
        </DialogField>
        <SwitchField
          id="product-allow-price-override"
          label="Allow Price Override"
          checked={form.allowPriceOverride}
          onChange={(v) => patch({ allowPriceOverride: v })}
          description="When on, staff can edit this product's price while charging a payment."
          badge={badge('allowPriceOverride')}
          className="pb-1 sm:col-span-2"
        />
      </div>

      {/* Shipping Fee — the hint on its own line, so a long fee name never pushes into it. */}
      <div className="grid gap-2">
        <div className="grid gap-4 sm:grid-cols-3">
          <DialogField label="Shipping Fee" htmlFor="product-shipping-fee" badge={badge('shippingFeeId')}>
            <Select<number>
              id="product-shipping-fee"
              options={shippingOptions}
              value={form.shippingFeeId}
              onChange={(v) => patch({ shippingFeeId: v ?? 0 })}
              placeholder="No shipping fee"
            />
          </DialogField>
        </div>
        <p className={DESCRIPTION_CLASS}>
          Charged once per payment, however many of these are bought. Never taxed or discounted.
        </p>
      </div>

      {/* Stock | Unit | Taxable | Inventory */}
      <div className="grid grid-cols-2 items-end gap-4 sm:grid-cols-4">
        <DialogField label="Stock" htmlFor="product-stock" badge={badge('stock')}>
          <Input
            id="product-stock"
            inputMode="numeric"
            placeholder="0"
            value={form.stock}
            onChange={(e) => patch({ stock: e.target.value })}
            error={errors.stock}
          />
        </DialogField>
        <DialogField label="Unit" htmlFor="product-unit" badge={badge('unit')}>
          <Input id="product-unit" placeholder="1" value={form.unit} onChange={(e) => patch({ unit: e.target.value })} />
        </DialogField>
        <SwitchField
          id="product-taxable"
          label="Taxable"
          checked={form.taxable}
          onChange={(v) => patch({ taxable: v })}
          badge={badge('taxable')}
          className="pb-2"
        />
        <SwitchField
          id="product-is-inventory"
          label="Inventory"
          checked={form.isInventory}
          onChange={(v) => patch({ isInventory: v })}
          badge={badge('isInventory')}
          className="pb-2"
        />
      </div>

      {form.isInventory && (
        <div className="grid gap-4 rounded-md border border-slate-200 bg-slate-50/50 p-4 sm:grid-cols-2">
          <DialogField label="PAR Level" htmlFor="product-par-level" badge={badge('parLevel')}>
            <Input
              id="product-par-level"
              inputMode="numeric"
              placeholder="10"
              value={form.parLevel}
              onChange={(e) => patch({ parLevel: e.target.value })}
              error={errors.parLevel}
            />
          </DialogField>
          <DialogField label="Verification Interval (days)" htmlFor="product-verify-interval" badge={badge('verifyInterval')}>
            <Input
              id="product-verify-interval"
              inputMode="numeric"
              placeholder="30"
              value={form.verifyInterval}
              onChange={(e) => patch({ verifyInterval: e.target.value })}
              error={errors.verifyInterval}
            />
          </DialogField>
        </div>
      )}
    </div>
  );
}

// ─── Category modal (add + rename) ─────────────────────────────────────────────

/** The products filed under a category, read-only (corp's edit dialog lists them). */
function CategoryProductList({ categoryId }: { categoryId: number }) {
  const [rows, setRows] = useState<categoriesSvc.CategoryProductPreview[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    categoriesSvc
      .listCategoryProducts(categoryId)
      .then((r) => {
        if (!cancelled) setRows(Array.isArray(r) ? r : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  return (
    <div className="grid gap-2">
      <span className={LABEL_CLASS}>
        <span>
          Products in this category
          {rows !== null && <span className="ml-1.5 font-normal text-slate-500">({rows.length})</span>}
        </span>
      </span>
      {rows === null ? (
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50/50 px-3 py-4 text-sm text-slate-500">
          <Spinner size="sm" />
          Loading products…
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-slate-200 bg-slate-50/50 px-3 py-4 text-sm italic text-slate-500">
          No products are filed under this category.
        </p>
      ) : (
        <ul
          data-testid="category-products"
          className="max-h-[220px] divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200"
        >
          {rows.map((p) => (
            <li key={p.id} className="flex items-baseline gap-2 px-3 py-1.5 text-sm">
              <span className="min-w-0 flex-1 truncate text-slate-800">{p.name || '(untitled)'}</span>
              <span className="shrink-0 text-xs tabular-nums text-slate-500">{formatCurrency(parseCurrency(p.price))}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CategoryModal({
  category,
  categories,
  onClose,
  onSaved,
}: {
  /** null = a new category. */
  category: BoardCategory | null;
  /** The account's categories, for the duplicate check (live ones only count). */
  categories: ProductItemCategory[];
  onClose: () => void;
  onSaved: (saved: ProductItemCategory, isNew: boolean) => void;
}) {
  const isNew = category === null;
  const [title, setTitle] = useState(category?.title ?? '');
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const conflict = findCategoryConflict(categories, title, category?.id ?? null);
  const shownError = error ?? (conflict ? categoryConflictMessage(title, conflict) : undefined);
  const blank = title.trim() === '';
  const fieldState: FieldState = blank || conflict || error ? 'error' : 'none';
  const requiredErrors = blank ? ['Category name is required'] : conflict ? ['Category name must be unique'] : [];

  async function handleSave() {
    const trimmed = title.trim();
    if (trimmed === '') {
      setError('Enter a category name.');
      return;
    }
    if (conflict) return;
    setSaving(true);
    try {
      const saved = category
        ? await categoriesSvc.updateProductCategory(category.id, trimmed)
        : await categoriesSvc.createProductCategory(trimmed);
      onSaved(saved, category === null);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message = getErrorMessage(err, 'The category could not be saved.');
      // 422 is the taken-title answer (DUPLICATE_TITLE) or a validation
      // message — both belong on the field.
      if (status === 422) setError(message);
      else toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={
        <DialogHeading
          title={isNew ? 'Add Category' : 'Edit Category'}
          variant={isNew ? 'default' : 'edit'}
          description={isNew ? 'Add a product category to this account.' : 'Edit the category name.'}
        />
      }
      size={isNew ? 'md' : 'lg'}
      footer={
        <DialogFooterLayout errors={requiredErrors} isCreating={isNew}>
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={blank || !!conflict}>
            {isNew ? 'Add' : 'Update'}
          </Button>
        </DialogFooterLayout>
      }
    >
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <DialogField
          label="Category Name"
          htmlFor="category-title"
          required
          state={fieldState}
          labelHint={blank ? '— required' : conflict || error ? '— name already used' : undefined}
        >
          <Input
            id="category-title"
            autoFocus
            placeholder="e.g. Surgical Supplies"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setError(undefined);
            }}
            error={shownError}
            className={fieldClasses.input(fieldState)}
          />
        </DialogField>
        {category && <CategoryProductList categoryId={category.id} />}
      </form>
    </Modal>
  );
}

// ─── Clone category modal ──────────────────────────────────────────────────────

function CloneCategoryModal({
  category,
  categories,
  facilities,
  onClose,
  onCloned,
}: {
  category: BoardCategory;
  categories: ProductItemCategory[];
  facilities: Facility[];
  onClose: () => void;
  onCloned: () => void;
}) {
  const [title, setTitle] = useState(`Copy of ${category.title}`);
  const [facilityIds, setFacilityIds] = useState<number[]>([]);
  const [preview, setPreview] = useState<categoriesSvc.CategoryProductPreview[] | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [cloning, setCloning] = useState(false);
  const activeFacilities = facilities.filter((f) => f.active);

  useEffect(() => {
    let cancelled = false;
    categoriesSvc
      .listCategoryProducts(category.id)
      .then((rows) => {
        if (!cancelled) setPreview(rows);
      })
      .catch(() => {
        if (!cancelled) setPreview([]);
      });
    return () => {
      cancelled = true;
    };
  }, [category.id]);

  const conflict = findCategoryConflict(categories, title);
  const blank = title.trim() === '';
  const shownError =
    error ??
    (conflict
      ? `This account already has a category named "${title.trim()}" (#${conflict.quoteItemCostsCategoryID}). Choose a different name.`
      : undefined);
  const cloneErrors = blank ? ['New category name is required'] : conflict ? ['New category name must be unique'] : [];
  const fieldState: FieldState = cloneErrors.length > 0 || error ? 'error' : 'none';

  async function handleClone() {
    const trimmed = title.trim();
    if (trimmed === '') {
      setError('Enter a name for the new category.');
      return;
    }
    if (conflict) return;
    setCloning(true);
    try {
      const { productsCloned } = await categoriesSvc.cloneProductCategory(category.id, trimmed, facilityIds);
      toast.success(`"${category.title}" copied to "${trimmed}" with ${plural(productsCloned, 'product', 'products')}`);
      onCloned();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message = getErrorMessage(err, 'The category could not be cloned.');
      if (status === 422) setError(message);
      else toast.error(message);
    } finally {
      setCloning(false);
    }
  }

  const count = preview?.length ?? 0;

  return (
    <Modal
      isOpen
      onClose={cloning ? () => {} : onClose}
      title={
        <DialogHeading
          title="Clone Product Category"
          variant="clone"
          description={
            <>
              This creates a copy of <strong>{category.title}</strong>, along with the products listed below and their
              settings (types, add-on pricing, plan associations, membership availability, and images). The copies keep
              their own names and are filed under the new category.
            </>
          }
        />
      }
      size="md"
      footer={
        <DialogFooterLayout errors={cloneErrors} isCreating>
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={cloning}>
            Cancel
          </Button>
          <Button
            leftIcon={<Copy className="h-4 w-4" />}
            onClick={() => void handleClone()}
            loading={cloning}
            disabled={preview === null || cloneErrors.length > 0}
          >
            Clone Category
          </Button>
        </DialogFooterLayout>
      }
    >
      <div className="space-y-4">
        <DialogField
          label="New Category Name"
          htmlFor="clone-category-title"
          required
          state={fieldState}
          labelHint={fieldState === 'error' ? (blank ? '— required' : '— must be unique') : undefined}
        >
          <Input
            id="clone-category-title"
            placeholder="New category name"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setError(undefined);
            }}
            error={shownError}
            className={fieldClasses.input(fieldState)}
          />
        </DialogField>

        {activeFacilities.length > 0 && (
          <div className="grid gap-2">
            <span className={LABEL_CLASS}>Available at facilities</span>
            <CheckboxList
              idPrefix="clone-fac"
              items={activeFacilities.map((f) => ({ id: f.facilityId, label: f.facilityName }))}
              selected={facilityIds}
              onChange={setFacilityIds}
              textSize="sm"
            />
            <p className={DESCRIPTION_CLASS}>
              {facilityIds.length === 0
                ? 'Leave all off and each copied product keeps the facilities its original was available at.'
                : `All ${count} copied product${count === 1 ? '' : 's'} will be available at ${facilityIds.length} of ${plural(activeFacilities.length, 'facility', 'facilities')}, replacing the originals' own.`}
            </p>
          </div>
        )}

        <div className="grid gap-2">
          <span className={LABEL_CLASS}>
            <span>
              Products to clone
              {preview !== null && <span className="ml-1.5 font-normal text-slate-500">({preview.length})</span>}
            </span>
          </span>
          {preview === null ? (
            <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50/50 px-3 py-4 text-sm text-slate-500">
              <Spinner size="sm" />
              Loading products…
            </div>
          ) : preview.length === 0 ? (
            <p className="rounded-md border border-slate-200 bg-slate-50/50 px-3 py-4 text-sm italic text-slate-500">
              No products in this category — only the category itself will be copied.
            </p>
          ) : (
            <ul data-testid="clone-preview" className="max-h-[220px] divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
              {preview.map((p) => (
                <li key={p.id} className="flex items-baseline gap-2 px-3 py-1.5 text-sm">
                  <span className="min-w-0 flex-1 truncate text-slate-800">{p.name || '(untitled)'}</span>
                  <span className="shrink-0 text-xs tabular-nums text-slate-500">{formatCurrency(parseCurrency(p.price))}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}


// ─── Board row ─────────────────────────────────────────────────────────────────

function ProductRow({
  product,
  facilities,
  deleted,
  onOpen,
}: {
  product: ProductFull;
  facilities: Facility[];
  deleted: boolean;
  onOpen: (p: ProductFull) => void;
}) {
  const ptName = product.relationships?.productTypeRelation?.productTypeName ?? null;
  const thumbUrl = getProductTypeImageUrl(product);
  const facIds = getProductFacilityIds(product, facilities);
  const activeFacCount = facilities.filter((f) => f.active).length;

  return (
    <button
      type="button"
      // A deleted product is restored, not edited.
      disabled={deleted}
      onClick={() => onOpen(product)}
      className="flex w-full items-center gap-3 py-2 pr-2 text-left hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
    >
      <div className="w-8 h-8 rounded border border-slate-200 bg-slate-50 flex-shrink-0 flex items-center justify-center overflow-hidden">
        {thumbUrl ? <img src={thumbUrl} alt="" className="w-full h-full object-cover" /> : <Package className="h-4 w-4 text-slate-400" />}
      </div>
      <div className="min-w-0 flex-1">
        <span data-testid="product-name" className="block truncate font-medium text-sm text-slate-700">
          {getProductName(product)}
        </span>
        {ptName && <span className="block truncate text-xs text-slate-400">{ptName}</span>}
      </div>
      {/* Corp's four flag columns: product type, image, description, in store. */}
      <span className="w-5 shrink-0" title={hasProductType(product) ? 'Has a product type' : undefined}>
        <FlagIcon on={hasProductType(product)} icon={<Tag />} className="text-blue-500" label="Has a product type" />
      </span>
      <span className="w-5 shrink-0" title={thumbUrl ? 'Has an image' : undefined}>
        <FlagIcon on={Boolean(thumbUrl)} icon={<ImageIcon />} className="text-green-500" label="Has an image" />
      </span>
      <span className="w-5 shrink-0" title={hasDescription(product) ? 'Has a description' : undefined}>
        <FlagIcon on={hasDescription(product)} icon={<FileText />} className="text-purple-500" label="Has a description" />
      </span>
      <span className="w-5 shrink-0" title={isAvailableInStore(product) ? 'Available in store' : undefined}>
        {isAvailableInStore(product) && (
          <Store data-testid="in-store-icon" aria-label="Available in store" className="h-4 w-4 mx-auto text-orange-500" />
        )}
      </span>
      <span className="hidden w-48 shrink-0 md:block">
        {facIds.length === activeFacCount && activeFacCount > 0 ? (
          <span className="text-xs text-slate-500">All facilities</span>
        ) : facIds.length === 0 ? (
          <span className="text-xs text-slate-400">No facilities</span>
        ) : (
          <span className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-slate-500">
              {facIds.length}/{activeFacCount}
            </span>
            {facIds.slice(0, 2).map((id) => {
              const fac = facilities.find((f) => f.facilityId === id);
              return (
                <span
                  key={id}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-200"
                >
                  {fac?.facilityName ?? `#${id}`}
                </span>
              );
            })}
            {facIds.length > 2 && <span className="text-[10px] text-slate-400">+{facIds.length - 2} more</span>}
          </span>
        )}
      </span>
      <span className="w-20 shrink-0 text-right text-sm text-slate-700">{formatCurrency(getPrice(product))}</span>
    </button>
  );
}

function hasDescription(p: ProductFull): boolean {
  return Boolean((p.strDescription ?? '').trim());
}

/** The board's sticky column header — the same widths as ProductRow. */
const PRODUCT_COLUMNS = (
  <div className="flex items-center gap-3 pr-2">
    <span className="w-8 shrink-0" aria-hidden="true" />
    <span className="min-w-0 flex-1">Name</span>
    <span className="w-5 shrink-0" title="Product Type">
      <Tag data-testid="product-type-column-icon" aria-label="Has a product type (column)" className="h-3.5 w-3.5 mx-auto text-slate-400" />
    </span>
    <span className="w-5 shrink-0" title="Product Type Image">
      <ImageIcon data-testid="image-column-icon" aria-label="Has an image (column)" className="h-3.5 w-3.5 mx-auto text-slate-400" />
    </span>
    <span className="w-5 shrink-0" title="Has Description">
      <FileText data-testid="description-column-icon" aria-label="Has a description (column)" className="h-3.5 w-3.5 mx-auto text-slate-400" />
    </span>
    <span className="w-5 shrink-0" title="Available in Store">
      <Store data-testid="store-column-icon" aria-label="Available in Store" className="h-3.5 w-3.5 mx-auto text-slate-400" />
    </span>
    <span className="hidden w-48 shrink-0 md:block">Facilities</span>
    <span className="w-20 shrink-0 text-right">Price</span>
  </div>
);

/**
 * A category header's cells, right-aligned under the product columns: corp's
 * summary of the four flag columns (coloured when every live product has it,
 * grey when only some do). Facilities and Price are blank on a category row.
 */
function CategoryColumns({ products }: { products: ProductFull[] }) {
  const live = products.filter((p) => isActive(p.active));
  const n = (pred: (p: ProductFull) => boolean) => live.filter(pred).length;
  return (
    <div className="flex shrink-0 items-center gap-3 pr-2" data-testid="category-summary">
      <span className="w-5 shrink-0">
        <SummaryIcon flagged={n(hasProductType)} total={live.length} icon={<Tag />} className="text-blue-500" label="has a product type" />
      </span>
      <span className="w-5 shrink-0">
        <SummaryIcon flagged={n((p) => Boolean(getProductTypeImageUrl(p)))} total={live.length} icon={<ImageIcon />} className="text-green-500" label="has an image" />
      </span>
      <span className="w-5 shrink-0">
        <SummaryIcon flagged={n(hasDescription)} total={live.length} icon={<FileText />} className="text-purple-500" label="has a description" />
      </span>
      <span className="w-5 shrink-0">
        <SummaryIcon flagged={n(isAvailableInStore)} total={live.length} icon={<Store />} className="text-orange-500" label="is in the store" />
      </span>
      <span className="hidden w-48 shrink-0 md:block" aria-hidden="true" />
      <span className="w-20 shrink-0" aria-hidden="true" />
    </div>
  );
}

// ─── Filters (the corp Products toolbar) ───────────────────────────────────────

/** The Product types filter's "No Product Type" choice (real choices are type ids). */
const NO_PRODUCT_TYPE = 'none';

const STORE_IN = 'in_store';
const STORE_OUT = 'not_in_store';
const STORE_AVAILABILITY_OPTIONS = [
  { value: STORE_IN, label: 'Available in Store' },
  { value: STORE_OUT, label: 'Not Available in Store' },
];

/** "Available To" (productAddon.availabilityStatus), as the product form labels it. */
const AUDIENCE_OPTIONS = AVAILABLE_TO_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }));

/** The product's "Available To" value as a string: '0' Everyone, '1' Members Only, '2' Non-Members Only. */
function getAudience(p: ProductFull): string {
  return String(Number(p.relationships?.productAddon?.availabilityStatus ?? 0) || 0);
}

// ─── Main Component ────────────────────────────────────────────────────────────

type ProductModalState = { kind: 'edit'; product: ProductFull } | { kind: 'new'; categoryId: number | null } | null;
type CategoryModalState = { kind: 'add' } | { kind: 'rename'; category: BoardCategory } | null;

export function ProductsAdmin() {
  // Locked: the shared blocks drop Add, the row icons and drag; the page only
  // stops talking about dragging and adding (silently, R18).
  const locked = useAdminPageLevel() === 'locked';
  const [products, setProducts] = useState<ProductFull[]>([]);
  const [categories, setCategories] = useState<ProductItemCategory[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [lookups, setLookups] = useState<Lookups>({ productTypes: [], shippingFees: [], membershipTemplates: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [productModal, setProductModal] = useState<ProductModalState>(null);
  const [categoryModal, setCategoryModal] = useState<CategoryModalState>(null);
  const [deletingCategory, setDeletingCategory] = useState<BoardCategory | null>(null);
  const [cloningCategory, setCloningCategory] = useState<BoardCategory | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<ProductFull | null>(null);
  // Off by default. On, deleted categories and products are listed too.
  const [showDeleted, setShowDeleted] = useState(false);
  // The corp toolbar's filters (nothing picked = no filtering). Ids are strings.
  const [facilityFilter, setFacilityFilter] = useState<string[]>([]);
  const [productTypeFilter, setProductTypeFilter] = useState<string[]>([]);
  const [storeFilter, setStoreFilter] = useState<string[]>([]);
  const [audienceFilter, setAudienceFilter] = useState<string[]>([]);

  // ── Data loading ─────────────────────────────────────────────────────

  // Only the latest load may write: flipping "Show deleted" quickly must not
  // let an older answer land last.
  const loadSeq = useRef(0);

  /**
   * withDeleted adds the deleted rows: GET /products answers active-only or
   * (active=0) deleted-only, so both are read and merged; the category filter
   * is lifted on both so products under a deleted category come back too.
   * quiet keeps the board mounted (and its open categories open).
   */
  const load = useCallback(async (withDeleted: boolean, quiet = false) => {
    const seq = ++loadSeq.current;
    if (!quiet) setLoading(true);
    try {
      const [productData, categoryData, facilityData] = await Promise.all([
        withDeleted
          ? Promise.all([
              productsSvc.listAllProducts({ includeDeletedCategories: true }),
              productsSvc.listAllProducts({ active: 0, includeDeletedCategories: true }),
            ]).then(([live, deleted]) => mergeProducts(live, deleted))
          : productsSvc.listAllProducts(),
        withDeleted ? categoriesSvc.listProductCategories({ includeDeleted: true }) : categoriesSvc.listProductCategories(),
        adminSvc.listFacilities(),
      ]);
      if (seq !== loadSeq.current) return;
      setProducts(productData);
      setCategories(categoryData);
      setFacilities(facilityData);
    } catch {
      if (seq === loadSeq.current) toast.error('Failed to load products');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  function handleShowDeletedChange(on: boolean) {
    setShowDeleted(on);
    void load(on, true);
  }

  useEffect(() => {
    void load(false);
    // The modal's pickers. Each is optional: a failed lookup leaves its list empty.
    void Promise.allSettled([
      inventorySvc.listProductTypes(),
      paymentsSvc.listShippingFees(),
      membershipsSvc.listTemplates(),
    ]).then(([types, fees, templates]) => {
      setLookups({
        productTypes: types.status === 'fulfilled' && Array.isArray(types.value) ? types.value : [],
        shippingFees: fees.status === 'fulfilled' && Array.isArray(fees.value) ? fees.value.filter((f) => f.active !== false) : [],
        membershipTemplates:
          templates.status === 'fulfilled' && Array.isArray(templates.value) ? templates.value.filter((t) => t.active !== false) : [],
      });
    });
  }, [load]);

  // ── Board data ───────────────────────────────────────────────────────

  const sortedCategories = useMemo(() => [...categories].sort(compareCategories), [categories]);

  // Separator categories ("--------") are never shown, nor their products.
  const separatorCategoryIds = useMemo(
    () => new Set(categories.filter((c) => isSeparatorTitle(c.title)).map((c) => c.quoteItemCostsCategoryID)),
    [categories],
  );

  // Show deleted lists everything the API sent except separators.
  const listed = useMemo(
    () =>
      products
        .filter(showDeleted ? (p) => !isSeparator(p) : isListed)
        .filter((p) => !inSeparatorCategory(p, separatorCategoryIds))
        .sort(compareProducts),
    [products, showDeleted, separatorCategoryIds],
  );

  const query = searchQuery.toLowerCase().trim();
  const searching = query !== '';

  // Every category the board can show (separators never; deleted ones only with Show deleted).
  const allBoardCategories = useMemo(
    (): BoardCategory[] =>
      sortedCategories
        .filter((c) => !isSeparatorTitle(c.title))
        .map((c) => ({ id: c.quoteItemCostsCategoryID, title: c.title, deleted: !isCategoryRowActive(c) })),
    [sortedCategories],
  );

  // ── Filters (corp parity: Facility, Categories, Product types, Store availability, Audience)

  const productTypeNameById = useMemo(
    () => new Map(lookups.productTypes.map((t) => [t.productTypeID, (t.productTypeName ?? '').trim()])),
    [lookups.productTypes],
  );

  // As corp: the product types the listed products carry, plus "No Product Type".
  const productTypeOptions = useMemo(() => {
    const byId = new Map<number, string>();
    for (const p of listed) {
      const id = getProductTypeId(p);
      if (id === null || byId.has(id)) continue;
      const name =
        productTypeNameById.get(id) || (p.relationships?.productTypeRelation?.productTypeName ?? '').trim() || `Product type #${id}`;
      byId.set(id, name);
    }
    const typed = [...byId.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]) || a[0] - b[0])
      .map(([id, name]) => ({ value: String(id), label: name }));
    return [{ value: NO_PRODUCT_TYPE, label: 'No Product Type' }, ...typed];
  }, [listed, productTypeNameById]);

  const filters: ListFilterConfig[] = [
    {
      id: 'products-facility-filter',
      label: 'Filter by facility',
      placeholder: 'All facilities',
      searchPlaceholder: 'Search facilities…',
      emptyMessage: 'No facilities found.',
      options: facilities.filter((f) => f.active).map((f) => ({ value: String(f.facilityId), label: f.facilityName })),
      selected: facilityFilter,
      onChange: setFacilityFilter,
    },
    {
      id: 'products-product-type-filter',
      label: 'Filter by product type',
      placeholder: 'All product types',
      searchPlaceholder: 'Search product types…',
      emptyMessage: 'No product types found.',
      options: productTypeOptions,
      selected: productTypeFilter,
      onChange: setProductTypeFilter,
    },
    {
      id: 'products-store-filter',
      label: 'Filter by store availability',
      placeholder: 'All store availability',
      options: STORE_AVAILABILITY_OPTIONS,
      selected: storeFilter,
      onChange: setStoreFilter,
    },
    {
      id: 'products-audience-filter',
      label: 'Filter by audience',
      placeholder: 'All audiences',
      options: AUDIENCE_OPTIONS,
      selected: audienceFilter,
      onChange: setAudienceFilter,
    },
  ];
  const filtering = anyFilterActive(filters);

  const records = useMemo(() => {
    if (!searching && !filtering) return listed;
    const facilitySet = new Set(facilityFilter.map(Number));
    const wantNoType = productTypeFilter.includes(NO_PRODUCT_TYPE);
    const typeSet = new Set(productTypeFilter.filter((v) => v !== NO_PRODUCT_TYPE).map(Number));
    return listed.filter((p) => {
      if (searching && !getProductName(p).toLowerCase().includes(query)) return false;
      if (facilitySet.size > 0 && !getProductFacilityIds(p, facilities).some((id) => facilitySet.has(id))) return false;
      if (productTypeFilter.length > 0) {
        const typeId = getProductTypeId(p);
        if (typeId === null ? !wantNoType : !typeSet.has(typeId)) return false;
      }
      if (storeFilter.length > 0 && !storeFilter.includes(isAvailableInStore(p) ? STORE_IN : STORE_OUT)) return false;
      if (audienceFilter.length > 0 && !audienceFilter.includes(getAudience(p))) return false;
      return true;
    });
  }, [listed, searching, filtering, query, facilityFilter, productTypeFilter, storeFilter, audienceFilter, facilities]);

  const productsByCategory = useMemo(() => {
    const map = new Map<number, ProductFull[]>();
    for (const p of records) {
      const c = getCategoryId(p);
      if (c !== null) map.set(c, [...(map.get(c) ?? []), p]);
    }
    return map;
  }, [records]);

  // A searched or filtered board shows only the categories holding a match.
  const narrowed = searching || filtering;
  const boardCategories = useMemo((): BoardCategory[] => {
    if (!narrowed) return allBoardCategories;
    const withMatches = new Set(records.map(getCategoryId));
    return allBoardCategories.filter((c) => withMatches.has(c.id));
  }, [allBoardCategories, records, narrowed]);

  const nextOrder = useCallback((categoryId: number | null) => nextOrderIn(products, categoryId), [products]);

  // ── Board saves (each returns the API promise; the board rolls back on reject)

  const handleReorderRecords = useCallback(
    async (categoryId: number, ids: number[]) => {
      await productsSvc.reorderProducts(categoryId, ids);
      const cat = categories.find((c) => c.quoteItemCostsCategoryID === categoryId);
      setProducts((prev) =>
        prev.map((p) => {
          const at = ids.indexOf(p.quoteItemCostID);
          if (at < 0) return p;
          return {
            ...p,
            quoteItemCostsCategoryID: categoryId,
            lngOrder: at + 1,
            relationships: {
              ...p.relationships,
              category: cat
                ? { quoteItemCostsCategoryID: categoryId, title: cat.title, active: 1 }
                : p.relationships.category,
            },
          };
        }),
      );
    },
    [categories],
  );

  const handleSaveError = useCallback((err: unknown) => {
    toast.error(getErrorMessage(err, 'The new order could not be saved.'));
  }, []);

  // ── Local state patchers ─────────────────────────────────────────────

  function handleFacilitiesChanged(productId: number, facilityIds: number[]) {
    setProducts((prev) => prev.map((p) => (p.quoteItemCostID === productId ? setProductFacilities(p, facilityIds, facilities) : p)));
  }

  /** A type added or edited from the product modal joins (or updates) the selector's list. */
  function handleProductTypeSaved(saved: ProductTypeDetail, isNew: boolean, imageUrl: string | null) {
    const fields = {
      productTypeID: saved.productTypeID,
      productTypeName: saved.productTypeName,
      productCredits: saved.productCredits,
      disburseType: saved.disburseType,
      description: saved.description,
    };
    setLookups((prev) => ({
      ...prev,
      productTypes: isNew
        ? [...prev.productTypes, { ...fields, status: 1, activeProductsCount: 0 }]
        : prev.productTypes.map((t) => (t.productTypeID === saved.productTypeID ? { ...t, ...fields } : t)),
    }));
    if (!isNew) {
      // Rows show the type's name under the product.
      setProducts((prev) =>
        prev.map((p) =>
          getProductTypeId(p) === saved.productTypeID && p.relationships?.productTypeRelation
            ? {
                ...p,
                relationships: {
                  ...p.relationships,
                  productTypeRelation: { ...p.relationships.productTypeRelation, productTypeName: saved.productTypeName },
                },
              }
            : p,
        ),
      );
    }
    if (imageUrl) handleProductTypeImageChanged(saved.productTypeID, imageUrl);
  }

  /** Every product of the type shows the new image (the board's thumbnails). */
  function handleProductTypeImageChanged(productTypeId: number, url: string) {
    setProducts((prev) => prev.map((p) => (getProductTypeId(p) === productTypeId ? setProductTypeImage(p, url) : p)));
  }

  function handleStoreChanged(productId: number, available: boolean) {
    setProducts((prev) => prev.map((p) => (p.quoteItemCostID === productId ? setProductStore(p, available) : p)));
  }

  function handleProductSaved(saved: ProductFull, isNew: boolean) {
    if (saved && typeof saved.quoteItemCostID === 'number') {
      setProducts((prev) =>
        isNew ? [...prev, saved] : prev.map((p) => (p.quoteItemCostID === saved.quoteItemCostID ? { ...p, ...saved } : p)),
      );
    } else {
      void load(showDeleted, true);
    }
    setProductModal(null);
  }

  /** Off the board — or, while deleted rows are shown, marked deleted in place. */
  function handleProductDeleted(productId: number) {
    setProducts((prev) =>
      showDeleted
        ? prev.map((p) => (p.quoteItemCostID === productId ? { ...p, active: 0 } : p))
        : prev.filter((p) => p.quoteItemCostID !== productId),
    );
    setProductModal(null);
  }

  async function handleDeleteProduct() {
    if (!deletingProduct) return;
    const id = deletingProduct.quoteItemCostID;
    try {
      await productsSvc.deleteProduct(id);
      toast.success('Product deleted');
      handleProductDeleted(id);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The product could not be deleted.'));
    }
  }

  async function handleRestoreProduct(p: ProductFull) {
    try {
      await productsSvc.restoreProduct(p.quoteItemCostID);
      toast.success('Product restored');
      await load(showDeleted, true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The product could not be restored.'));
    }
  }

  /** A 422 here is DUPLICATE_TITLE: an active category already has the name. */
  async function handleRestoreCategory(c: BoardCategory) {
    try {
      await categoriesSvc.restoreProductCategory(c.id);
      toast.success('Category restored');
      await load(showDeleted, true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The category could not be restored.'));
    }
  }

  /** A new or renamed category joins the board (from the toolbar or the product form). */
  function applyCategorySaved(saved: ProductItemCategory, isNew: boolean) {
    setCategories((prev) => {
      if (isNew) {
        const maxOrder = prev.reduce((m, c) => Math.max(m, Number(c.qiOrder) || 0), 0);
        return [...prev, { ...saved, qiOrder: saved.qiOrder ?? maxOrder + 1 }];
      }
      return prev.map((c) => (c.quoteItemCostsCategoryID === saved.quoteItemCostsCategoryID ? { ...c, title: saved.title } : c));
    });
    toast.success(isNew ? 'Category added' : 'Category saved');
  }

  function handleCategorySaved(saved: ProductItemCategory, isNew: boolean) {
    applyCategorySaved(saved, isNew);
    setCategoryModal(null);
  }

  /** Active products filed under a category — all hidden once it is deleted. */
  function productsIn(categoryId: number): number {
    return products.filter((p) => isActive(p.active) && getCategoryId(p) === categoryId).length;
  }

  async function handleDeleteCategory() {
    if (!deletingCategory) return;
    const id = deletingCategory.id;
    try {
      await categoriesSvc.deleteProductCategory(id);
      if (showDeleted) {
        // Shown as deleted, its products still under it.
        setCategories((prev) => prev.map((c) => (c.quoteItemCostsCategoryID === id ? { ...c, active: 0 } : c)));
      } else {
        setCategories((prev) => prev.filter((c) => c.quoteItemCostsCategoryID !== id));
        // The products list hides products in a deleted category; match it.
        setProducts((prev) => prev.filter((p) => getCategoryId(p) !== id));
      }
      toast.success('Category deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The category could not be deleted.'));
    }
  }

  // ── Render ───────────────────────────────────────────────────────────

  const renderRecord = useCallback(
    (p: ProductFull) => (
      <ProductRow
        product={p}
        facilities={facilities}
        deleted={isProductDeleted(p)}
        onOpen={(prod) => setProductModal({ kind: 'edit', product: prod })}
      />
    ),
    [facilities],
  );

  // A deleted category offers only Restore.
  function renderCategoryActions(c: BoardCategory) {
    if (c.deleted) {
      return <AdminRowAction kind="restore" label={`Restore ${c.title}`} onClick={() => void handleRestoreCategory(c)} />;
    }
    return (
      <>
        <AdminRowAction
          kind="add"
          title="Add product"
          label={`Add product to ${c.title}`}
          onClick={() => setProductModal({ kind: 'new', categoryId: c.id })}
        />
        <AdminRowAction kind="edit" label={`Edit ${c.title}`} onClick={() => setCategoryModal({ kind: 'rename', category: c })} />
        <AdminRowAction kind="clone" label={`Clone ${c.title}`} onClick={() => setCloningCategory(c)} />
        <AdminRowAction kind="delete" label={`Delete ${c.title}`} onClick={() => setDeletingCategory(c)} />
      </>
    );
  }

  // A deleted product offers only Restore.
  function renderRecordActions(p: ProductFull) {
    const name = getProductName(p);
    if (isProductDeleted(p)) {
      return <AdminRowAction kind="restore" label={`Restore product ${name}`} onClick={() => void handleRestoreProduct(p)} />;
    }
    return (
      <>
        <AdminRowAction kind="edit" label={`Edit product ${name}`} onClick={() => setProductModal({ kind: 'edit', product: p })} />
        <AdminRowAction kind="delete" label={`Delete product ${name}`} onClick={() => setDeletingProduct(p)} />
      </>
    );
  }

  // The modal's product is read from state, so store/facility saves made
  // inside it show there too.
  const editing =
    productModal?.kind === 'edit'
      ? products.find((p) => p.quoteItemCostID === productModal.product.quoteItemCostID) ?? productModal.product
      : null;

  const hiddenCount = deletingCategory ? productsIn(deletingCategory.id) : 0;

  return (
    <AdminBoardPage
      title="Products"
      subtitle={
        locked
          ? 'Products grouped by category.'
          : 'Products grouped by category. Drag a product onto another category to move it there.'
      }
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCategoryModal({ kind: 'add' })}>
          Add category
        </Button>
      }
      toolbar={
        <AdminListToolbar
          className="mb-0"
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by product name…"
          searchLabel="Search products"
          showDeleted={showDeleted}
          onShowDeletedChange={handleShowDeletedChange}
          showDeletedId="products-show-deleted"
        >
          <ListFilterBar filters={filters} />
        </AdminListToolbar>
      }
      hint={boardDragHint({ searching, filtering, showDeleted })}
      dialogs={
        <>
          {productModal && (
            <ProductModal
              key={productModal.kind === 'edit' ? `edit-${productModal.product.quoteItemCostID}` : 'new'}
              product={editing}
              defaultCategoryId={productModal.kind === 'new' ? productModal.categoryId : null}
              categoryTitle={
                productModal.kind === 'new'
                  ? categories.find((c) => c.quoteItemCostsCategoryID === productModal.categoryId)?.title
                  : undefined
              }
              facilities={facilities}
              lookups={lookups}
              nextOrder={nextOrder}
              onClose={() => setProductModal(null)}
              onSaved={handleProductSaved}
              onFacilitiesChanged={handleFacilitiesChanged}
              onStoreChanged={handleStoreChanged}
              onProductTypeSaved={handleProductTypeSaved}
              onProductTypeImageChanged={handleProductTypeImageChanged}
            />
          )}

          {categoryModal && (
            <CategoryModal
              category={categoryModal.kind === 'rename' ? categoryModal.category : null}
              categories={categories}
              onClose={() => setCategoryModal(null)}
              onSaved={handleCategorySaved}
            />
          )}

          {cloningCategory && (
            <CloneCategoryModal
              category={cloningCategory}
              categories={categories}
              facilities={facilities}
              onClose={() => setCloningCategory(null)}
              onCloned={() => {
                setCloningCategory(null);
                void load(showDeleted, true);
              }}
            />
          )}

          <ConfirmDialog
            isOpen={deletingCategory !== null}
            onClose={() => setDeletingCategory(null)}
            onConfirm={handleDeleteCategory}
            title="Delete category?"
            message={
              deletingCategory
                ? `Delete "${deletingCategory.title}"?` +
                  (hiddenCount > 0
                    ? ` ${plural(hiddenCount, 'product', 'products')} will be hidden until it is restored.`
                    : '')
                : ''
            }
            confirmLabel="Delete category"
          />

          <ConfirmDialog
            isOpen={deletingProduct !== null}
            onClose={() => setDeletingProduct(null)}
            onConfirm={handleDeleteProduct}
            title="Delete product?"
            message={deletingProduct ? `${getProductName(deletingProduct)} will be removed from Products and the store.` : ''}
            confirmLabel="Delete product"
          />
        </>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading products…" />
      ) : narrowed && records.length === 0 ? (
        <AdminListEmpty title="No products found" hint="Try adjusting your search or filters." />
      ) : (
        <>
          {!narrowed && boardCategories.length === 0 && records.length === 0 && (
            <AdminListEmpty title="No categories yet" hint={locked ? undefined : 'Add a category, then add products to it.'} />
          )}
          <CategoryBoard<ProductFull>
            categories={boardCategories}
            records={records}
            getRecordId={getRecordId}
            getRecordTitle={getProductName}
            getRecordCategoryId={getCategoryId}
            renderRecord={renderRecord}
            columnHeader={PRODUCT_COLUMNS}
            renderCategoryColumns={(c) => <CategoryColumns products={productsByCategory.get(c.id) ?? []} />}
            renderCategoryActions={renderCategoryActions}
            renderRecordActions={renderRecordActions}
            isRecordDeleted={isProductDeleted}
            onReorderRecords={handleReorderRecords}
            onSaveError={handleSaveError}
            recordNoun={['product', 'products']}
            // A list holding deleted rows (or a searched / filtered one) is never saved as an order.
            dragDisabled={narrowed || showDeleted}
          />
        </>
      )}
    </AdminBoardPage>
  );
}
